import React, { ReactNode, ReactNodeArray } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useSelector } from 'react-redux';
import { useAuthContext } from 'components/app/components/auth/auth';
import { When } from 'packages/when/when';
import { CUSTOMER_FORM_ID } from 'global/constants';
import { SummaryClient } from '../client';
import { SummaryCompany } from '../company';
import { SummaryOwnerShares } from '../owner-shares';
import { SummaryReservation } from '../reservation';
import { SummaryRepresentatives } from '../representatives';
import { SummaryFiscalYear } from '../fiscal-year';
import { SummaryAuditor } from '../auditor/auditor';
import { SummaryProxies } from '../proxies';
import { SummaryCostStatement } from '../cost-statement/cost-statement';
import { RootState } from '../../../../../../global';
import { SummarySubmissionInformation } from '../submission-information/submission-information';
import { FormSectionInfo } from '../../../../../form/components';
import { FormSectionContainer } from '../../../../../form/components/form-section-container/form-section-container';
import { BuyProcessUtils } from '../../../../index';
import { selectBuyAuditorPersonFactory } from '../../../../../../services/buy-process/selectors';
import { SummaryDisplayProps } from './summary-display-types';

export const SummaryDisplay = ({ orderId }: SummaryDisplayProps) => {
  const initialState = useSelector(
    (state: RootState) => state.buyOrderEdit[orderId],
  );

  const { nameReview, taxHelp, beneficialOwnerHelp } = useSelector(
    BuyProcessUtils.selectExtraServices(orderId),
  );

  BuyProcessUtils.useFetchExtraServicesCost({
    nameReview,
    taxHelp,
    beneficial: beneficialOwnerHelp,
  });

  const {
    clientInfo,
    companyDetails,
    costStatement,
    fiscalYear,
    representatives,
    reservation,
  } = initialState;

  const auth = useAuthContext();
  const auditorPerson = useSelector(selectBuyAuditorPersonFactory(orderId));
  const { auditorOption, auditorFirm } = useSelector(
    (state: RootState) => state.buyOrderEdit[orderId].auditor,
  );
  const shouldDisplaySubmissionInfo = auth.isAuthenticated && CUSTOMER_FORM_ID !== orderId;

  return (
    <React.Fragment>
      <When condition={shouldDisplaySubmissionInfo}>
        <SummaryContainer title="Inlämningsinformation">
          <SummarySubmissionInformation orderId={orderId} />
        </SummaryContainer>
      </When>

      <SummaryContainer title="Kontaktperson/Beställare">
        <SummaryClient orderId={orderId} clientInfo={clientInfo} />
      </SummaryContainer>

      <SummaryContainer title="Bolagsuppgifter">
        <SummaryCompany companyDetails={companyDetails} />
      </SummaryContainer>

      <SummaryContainer title="Ägare / Aktiekapital">
        <SummaryOwnerShares orderId={orderId} />
      </SummaryContainer>

      <SummaryContainer title="Förbehåll">
        <SummaryReservation reservation={reservation} />
      </SummaryContainer>

      <SummaryContainer title="Företrädare">
        <SummaryRepresentatives
          orderId={orderId}
          representatives={representatives}
        />
      </SummaryContainer>

      <SummaryContainer title="Räkenskapsår">
        <SummaryFiscalYear fiscalYear={fiscalYear} />
      </SummaryContainer>

      <SummaryContainer title="Revisor">
        <SummaryAuditor
          auditor={{
            auditorFirm,
            auditorOption,
            auditorPerson,
          }}
        />
      </SummaryContainer>

      <SummaryContainer title="Fullmakter">
        <SummaryProxies orderId={orderId} />
      </SummaryContainer>

      <SummaryContainer title="Kostnadssammanställning">
        <SummaryCostStatement costStatement={costStatement} />
      </SummaryContainer>
    </React.Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  sectionContainer: {
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(1),
  },
}));

type SummaryContainerProps = {
  title: string;
  children: ReactNodeArray | ReactNode;
};

function SummaryContainer(props: SummaryContainerProps) {
  const classes = useStyles();

  return (
    <React.Fragment>
      <FormSectionInfo.InfoBoxTitled title={props.title} />

      <FormSectionContainer className={classes.sectionContainer}>
        {props.children}
      </FormSectionContainer>
    </React.Fragment>
  );
}
